import React from 'react';
import { Box, TextField, MenuItem, FormControl, InputLabel, Select, FormControlLabel, Checkbox } from '@mui/material';
import dayjs from 'dayjs';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../../utils/timezoneUtils';
import { tlbFormatFieldProps, formatTechlogOilQuantityLabel } from '../../../utils/technicalLogBookFieldFormat';

/**
 * Renders the Additional (custom fields) tab from the format definition.
 * Binds to formData.additionalFields[key].
 */
const AdditionalFieldsTab = ({ formData, isReadOnly, updateField, additionalSection, displayTimezone }) => {
  if (!additionalSection?.customFields?.length) return null;

  const additionalFields = formData?.additionalFields || {};
  const tzLabel = displayTimezone ? getTimezoneLabel(displayTimezone) : '';
  const dateDisplayValue = (v) => {
    if (!v) return '';
    if (displayTimezone) {
      const d = utcToDisplayDateAndTime(v, displayTimezone).date;
      return d || dayjs(v).format('YYYY-MM-DD');
    }
    return dayjs(v).format('YYYY-MM-DD');
  };
  const dateSaveValue = (dateStr) => {
    if (!dateStr) return null;
    if (displayTimezone) return displayDateTimeToUTC(dateStr, '00:00', displayTimezone);
    return new Date(dateStr);
  };

  const normalizeHoursValue = (raw) => {
    if (!raw) return '';
    let value = String(raw).trim();
    if (!value) return '';
    if (!value.includes(':')) {
      value = value.slice(0, 6);
      if (!value) return '';
      return `${value}:00`;
    }
    const [hRaw, mRaw = ''] = value.split(':');
    const hours = hRaw.replace(/\D/g, '').slice(0, 6);
    const minutes = mRaw.replace(/\D/g, '').slice(0, 4);
    if (!hours) return '';
    return minutes ? `${hours}:${minutes}` : hours;
  };

  const renderControl = (field) => {
    const value = additionalFields[field.key];
    const path = `additionalFields.${field.key}`;
    const disabled = isReadOnly || field.readOnly;
    const label = formatTechlogOilQuantityLabel(field.label, field.key);

    switch (field.type) {
      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value === '' ? undefined : Number(e.target.value))}
            disabled={disabled}
            required={!!field.required}
          />
        );
      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            label={tzLabel ? `${label} (${tzLabel})` : label}
            value={dateDisplayValue(value)}
            onChange={(e) => updateField(path, e.target.value ? dateSaveValue(e.target.value) : null)}
            disabled={disabled}
            required={!!field.required}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'time':
        return (
          <TextField
            fullWidth
            type="time"
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value)}
            disabled={disabled}
            required={!!field.required}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => updateField(path, e.target.checked)}
                disabled={disabled}
              />
            }
            label={label}
          />
        );
      case 'dropdown':
        return (
          <FormControl fullWidth disabled={disabled} required={!!field.required}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={value ?? ''}
              label={label}
              onChange={(e) => updateField(path, e.target.value)}
            >
              {(field.options || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label ?? opt.value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'multiline':
        return (
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value)}
            disabled={disabled}
            required={!!field.required}
          />
        );
      case 'hours':
        return (
          <TextField
            fullWidth
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value)}
            onBlur={(e) => updateField(path, normalizeHoursValue(e.target.value))}
            disabled={disabled}
            required={!!field.required}
            {...tlbFormatFieldProps(value, { fieldType: 'hours' })}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value)}
            disabled={disabled}
            required={!!field.required}
          />
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {additionalSection.customFields
        .filter((f) => f.visible !== false)
        .map((field) => (
          <Box key={field.key}>{renderControl(field)}</Box>
        ))}
    </Box>
  );
};

export default AdditionalFieldsTab;
